<script lang="ts">
  import { Activity } from '@/Common/ActivityFunctions';
  import Matchstick from '@/Common/Matchstick.svelte';
  import { cn } from '@/lib/utils';
  import { convertSeconds, kmToMile, tidy, type XSSDetails } from '@/Util/utils';
  import XSSCircle from '../TrainingForecast/XSSCircle.svelte';
  import { Skeleton } from '@/lib/components/ui/skeleton';
  import { format } from 'date-fns';
  import DifficultyDiamonds from '@/DifficultyDiamonds.svelte';
  import { CalendarLoader, type LoaderActivity } from './CalendarLoaderNew';
  import * as Dialog from '$lib/components/ui/dialog';
  import { Button } from '@/lib/components/ui/button';
  import CalendarActivityCard from './CalendarActivityCard.svelte';
  import { CreateCustomEventDispatchHandler } from '@/EventDispatchers';
  import { onMount } from 'svelte';

  export let loaderActivities: LoaderActivity[] = [];
  $: activities = loaderActivities.map((activity) => activity.activity);
  export let date: Date = null;
  export let conflictDay: boolean = false; // TODO (hyde): indicates if a forecast activity is in a conflicting state
  export let layoutType: 'vertical' | 'horizontal' = 'vertical'; // determines whether the image is rendered above the content or to the right of the content
  export let onDrag: (x: number, y: number) => void;
  export let calendarLoader: CalendarLoader;

  //@ts-ignore
  const Xert = window.Xert;
  let useFreedomUnits: boolean = Xert._displayUnits == 'imperial';
  let rootDiv: HTMLElement;

  let activityNumbersContainer: ResizeObserverSize[] | null = null;
  let activityNumbersWidth: number | null = null;
  $: if (activityNumbersContainer && activityNumbersContainer[0]) activityNumbersWidth = activityNumbersContainer[0].inlineSize;
  $: useCompactNumbersLayout = activityNumbersWidth < 300;

  $: isStale = loaderActivities.some((activity) => activity.isStale);

  $: duration = activities.reduce((sum, activity) => sum + (activity.duration ?? activity.adu ?? 0), 0);
  $: distance = activities.reduce((sum, activity) => sum + (activity.distance ?? 0), 0);
  $: maxDifficulty = activities.reduce((max, activity) => (activity.difficulty ? Math.max(max, activity.difficulty) : max), 0);

  // TODO (hyde): use smarter xss details for the XSSCircle
  $: xssDetails = activities.reduce(
    (details, activity) => {
      details.xss += activity.xss ?? 0;
      details.xlss += activity.xlss ?? 0;
      details.xhss += activity.xhss ?? 0;
      details.xpss += activity.xpss ?? 0;
      return details;
    },
    { xss: 0, xlss: 0, xhss: 0, xpss: 0 }
  );
  $: completedActivities = activities.filter((activity) => !Activity.isPlanned(activity));
  $: completedXSSDetails = completedActivities.reduce(
    (details: XSSDetails<number>, activity) => {
      details.xss += activity.xss ?? 0;
      details.xlss += activity.xlss ?? 0;
      details.xhss += activity.xhss ?? 0;
      details.xpss += activity.xpss ?? 0;
      return details;
    },
    { xss: 0, xlss: 0, xhss: 0, xpss: 0 }
  );
  $: scheduledActivities = activities.filter((activity) => Activity.isPlanned(activity));
  $: scheduledXSSDetails = scheduledActivities.reduce(
    (details: XSSDetails<number>, activity) => {
      details.xss += activity.xss ?? 0;
      details.xlss += activity.xlss ?? 0;
      details.xhss += activity.xhss ?? 0;
      details.xpss += activity.xpss ?? 0;
      return details;
    },
    { xss: 0, xlss: 0, xhss: 0, xpss: 0 }
  );

  let dialogOpen = false;

  // TODO (hyde): don't do this to open the edit modal, this is bad old code
  let eventDispatchHandler;
  onMount(()=>{
    //Returns custom Event Dispatchers that can be used directly as event handlers
    //e.g. on:click={eventDispatcherHandler('myEvent', {...myEventDetails}, bubbles)}

    //Dispatches Custom Events that can be used within component methods
    //e.g. function myMethod() {/*modify component before call*/...; dispatchTrainingEvent('myEvent', {...myEventDetails}, bubbles); /*modify component after call*/  } 
    [eventDispatchHandler] = CreateCustomEventDispatchHandler(rootDiv);
  })

</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  bind:this={rootDiv}
  class={cn('w-full flex flex-row gap-0 rounded-sm', 'border border-border', 'hover:shadow-sm transition-shadow bg-muted cursor-pointer')}
  on:click={() => {
    dialogOpen = true;
  }}
>
  <Matchstick width={4} {conflictDay} xss={xssDetails.xss} xlss={xssDetails.xlss} xpss={xssDetails.xpss} xhss={xssDetails.xhss} />
  <div class={cn('flex-1 min-w-0 w-full flex flex-row items-center gap-2', layoutType == 'horizontal' ? 'pr-2' : '')}>
    <div class="flex flex-col gap-1 p-1 flex-1 min-w-0">
      <h3 class="font-semibold m-0 text-xs line-clamp-2">
        {activities.length} Activities
      </h3>
      <!-- {#if activity.focus ?? activity.fa}
        <span class="text-[10px] font-medium">{activity.spr} {activity.focus ?? activity.fa}</span>
      {/if} -->
      <div class="flex flex-row items-center gap-2">
        {#if scheduledActivities.length == 0}
          <XSSCircle loading={isStale} primaryXSSDetails={completedXSSDetails} primaryTitle={'Completed'} />
        {:else if completedActivities.length == 0}
          <XSSCircle loading={isStale} primaryXSSDetails={scheduledXSSDetails} primaryTitle={'Scheduled'} />
        {:else}
          <XSSCircle loading={isStale} primaryXSSDetails={completedXSSDetails} primaryTitle={'Completed'} targetXSSDetails={scheduledXSSDetails} secondaryTitle={'Scheduled'} />
        {/if}
        <div bind:borderBoxSize={activityNumbersContainer} class={cn('flex-1 min-w-0 max-w-[300px] flex', useCompactNumbersLayout ? 'flex-col items-end text-right' : 'flex-row gap-4')}>
          {#if useCompactNumbersLayout}
            {#if distance}
              {#if isStale}
                <Skeleton class="h-[11px] w-[50px]" />
              {:else}
                <span class="text-[10px] font-medium">{tidy(useFreedomUnits ? kmToMile(distance) : distance)} {useFreedomUnits ? 'mi' : 'km'}</span>
              {/if}
            {/if}
            {#if isStale}
              <Skeleton class="h-[11px] w-[30px]" />
            {:else}
              <span class="text-[10px] font-medium">{convertSeconds(duration)}</span>
            {/if}
            {#if maxDifficulty}
              <DifficultyDiamonds difficulty={maxDifficulty} compact={true} />
            {/if}
          {:else}
            <div class="flex flex-col gap-0">
              <span class="text-[10px] font-medium uppercase text-muted-foreground">Duration</span>
              {#if isStale}
                <Skeleton class="h-[11px] w-[30px]" />
              {:else}
                <span class="text-[14px] font-medium">{convertSeconds(duration)}</span>
              {/if}
            </div>
            {#if distance}
              <div class="flex flex-col gap-0">
                <span class="text-[10px] font-medium uppercase text-muted-foreground">Distance</span>
                {#if isStale}
                  <Skeleton class="h-[11px] w-[50px]" />
                {:else}
                  <span class="text-[14px] font-medium">{tidy(useFreedomUnits ? kmToMile(distance) : distance)} {useFreedomUnits ? 'mi' : 'km'}</span>
                {/if}
              </div>
            {/if}
            {#if maxDifficulty}
              <div class="flex flex-col gap-0">
                <span class="text-[10px] font-medium uppercase text-muted-foreground">Difficulty</span>
                <DifficultyDiamonds difficulty={maxDifficulty} compact={useCompactNumbersLayout} />
              </div>
            {/if}
          {/if}
        </div>
      </div>
    </div>
  </div>
</div>

<Dialog.Root bind:open={dialogOpen} portal="#shadcn-ui-dialog-target">
  <Dialog.Content class="clickable flex flex-col items-center gap-0 max-h-[95dvh] max-w-[800px] p-0 overflow-hidden rounded-xl">
    <!-- Header -->
    <div class="flex w-full items-start gap-4 px-8 pt-4 pb-3 border-b border-border">
      <div class="flex-1 min-w-0">
        <h2 class="text-xl font-semibold leading-tight truncate">
          {format(date ?? new Date(), 'do MMMM yyyy')}
        </h2>
      </div>
    </div>

    <div class='w-full flex-1 overflow-auto flex flex-col gap-2 p-4'
      on:editEvent={e => {
        // This is necessary so the edit event propagates out of the modal and into the actual calendar that has the event handler
        // And to close the modal so that it doesn't block the edit modal
        dialogOpen = false;
        eventDispatchHandler('editEvent', e.detail)();
      }}
    >
      {#each loaderActivities as la (la.activity.path)}
        <CalendarActivityCard activity={la.activity} isStale={la.isStale} layoutType='horizontal' calendarLoader={calendarLoader}
          onDrag={(x, y) => {
            if (dialogOpen) dialogOpen = false;
            onDrag(x, y);
          }}
        />
      {/each}
    </div>

    <!-- Footer actions -->
    <div class="flex w-full flex-row justify-between gap-3 pl-8 pr-5 py-4 border-t border-border bg-muted/30">
      <div class="flex-1"></div>
      <div class="flex gap-2 justify-end">
        <Button variant="outline" on:click={() => dialogOpen = false}>Close</Button>
      </div>
    </div>
  </Dialog.Content>
</Dialog.Root>

<style lang="scss">
  
  .clickable {
    transition: transform 150ms ease, box-shadow 150ms ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
    }

    &:active {
      transform: translateY(0);
      box-shadow: none;
    }
  }

</style>
